import React, { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CustomModal from "../widgets/CustomModal";
import { HeadingContext } from "../context/HeadingContext";
import AppContext from "../context/AppContext";
import {
  subscriptionList,
  cancelSubscription,
  resumeSubscription,
} from "../_slice/manageSubscriptionSlice";

// Format date consistent with project (e.g. 01/15/2026)
const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

// Status badge styling
const getStatusBadgeStyle = (status) => {
  const base = {
    padding: "4px 12px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: "600",
    textTransform: "capitalize",
    display: "inline-block",
    minWidth: "70px",
    textAlign: "center",
  };
  const palette = {
    active: { background: "#DCFCE7", color: "#166534" },
    trialing: { background: "#DBEAFE", color: "#1E40AF" },
    canceled: { background: "#E5E7EB", color: "#4B5563" },
    cancelled: { background: "#E5E7EB", color: "#4B5563" },
    past_due: { background: "#FEE2E2", color: "#B91C1C" },
  };
  return {
    ...base,
    ...(palette[status?.toLowerCase()] || {
      background: "#F3F4F6",
      color: "#374151",
    }),
  };
};

function ManageSubscription() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setLoader } = useContext(AppContext);
  const { setHeading } = useContext(HeadingContext);

  const { subscriptionData, status } = useSelector(
    (state) => state.manageSubscription
  );

  const [cancelModal, setCancelModal] = useState({ show: false, id: "" });
  const [resumeModal, setResumeModal] = useState({ show: false, id: "" });

  useEffect(() => {
    setHeading("Manage Subscription");
  }, [setHeading]);

  useEffect(() => {
    dispatch(subscriptionList({ navigate, setLoader }));
  }, []);

  const refreshList = () => {
    dispatch(subscriptionList({ navigate, setLoader }));
  };

  const handleCancel = () => {
    const id = cancelModal.id;
    setCancelModal({ show: false, id: "" });
    dispatch(
      cancelSubscription({
        id,
        cancelImmediately: false,
        navigate,
        setLoader,
      })
    ).then((response) => {
      if (response?.type?.endsWith("/fulfilled")) refreshList();
    });
  };

  const handleResume = () => {
    const id = resumeModal.id;
    setResumeModal({ show: false, id: "" });
    dispatch(resumeSubscription({ id, navigate, setLoader })).then(
      (response) => {
        if (response?.type?.endsWith("/fulfilled")) refreshList();
      }
    );
  };

  const rows = subscriptionData?.data || [];

  return (
    <>
      <div className="org">
        <div className="org_header d-flex align-items-center justify-content-between">
          <h1 className="org_header_heading">Manage Subscription</h1>
        </div>

        <div className="org_table table-responsive table-border-0">
          <table className="table">
            <thead>
              <tr>
                <td className="org_table_column w-40 zone_table_heading">
                  Location Name
                </td>
                <td className="org_table_column w-30 zone_table_heading">
                  Amount Paid
                </td>
                <td className="org_table_column w-30 text-center zone_table_heading">
                  Action
                </td>
              </tr>
            </thead>
            <tbody>
              {rows.length > 0 ? (
                rows.map((item) => {
                  const id = item.id || item.subscription_id;
                  const isCanceled = !!item.canceled_at;
                  return (
                    <tr key={`Subscription_${id}`}>
                      <td className="org_table_row w-40 zone_body_text">
                        {item.location_name || "-"}
                      </td>
                      <td className="org_table_row w-30 zone_body_text">
                        {item.monthly_display || "-"}
                      </td>
                      <td className="org_table_row w-30 zone_body_text">
                        <div className="d-flex align-items-center justify-content-center">
                          <button
                            type="button"
                            className="submit_btn"
                            style={{
                              whiteSpace: "nowrap",
                              padding: "6px 14px",
                              fontSize: "13px",
                              opacity: isCanceled ? 0.5 : 1,
                              cursor: isCanceled ? "not-allowed" : "pointer",
                            }}
                            disabled={isCanceled}
                            onClick={() => {
                              if (isCanceled) return;
                              setCancelModal({ show: true, id });
                            }}
                          >
                            {isCanceled ? "Canceled" : "Cancel Subscription"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={3}
                    className="org_table_row zone_body_text text-center"
                    style={{ padding: "30px", color: "#6B7280" }}
                  >
                    {status === "loading"
                      ? "Loading..."
                      : "No subscriptions found."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Cancel Confirmation Modal */}
      <CustomModal
        show={cancelModal.show}
        onHide={() => setCancelModal({ show: false, id: "" })}
        size="md"
        body={
          <div className="deleteModal">
            <h2
              className="text-center"
              style={{
                fontSize: "22px",
                fontWeight: "700",
                color: "#292929",
                marginBottom: "16px",
              }}
            >
              Cancel Subscription
            </h2>
            <p
              className="text-center mb-5"
              style={{ color: "#292929", fontSize: "14px", lineHeight: "1.6" }}
            >
              If you cancel your subscription, the quest markers won't work
              anymore. Are you sure you want to cancel this subscription?
            </p>
            <div className="d-flex justify-content-center align-items-center custom_bottom_padding">
              <button
                type="button"
                className="cancleBtn mr-3"
                onClick={() => setCancelModal({ show: false, id: "" })}
              >
                Cancel
              </button>
              <button
                type="button"
                className="submit_btn"
                style={{ whiteSpace: "nowrap" }}
                onClick={handleCancel}
              >
                Cancel Subscription
              </button>
            </div>
          </div>
        }
      />

      {/* Resume Confirmation Modal */}
      <CustomModal
        show={resumeModal.show}
        onHide={() => setResumeModal({ show: false, id: "" })}
        size="md"
        body={
          <div className="deleteModal">
            <p className="text-center mb-5">
              Are you sure you want to resume this subscription?
            </p>
            <div className="d-flex justify-content-between align-items-center custom_bottom_padding">
              <button
                type="button"
                className="submit_btn"
                onClick={handleResume}
              >
                Yes
              </button>
              <button
                type="button"
                className="cancleBtn"
                onClick={() => setResumeModal({ show: false, id: "" })}
              >
                No
              </button>
            </div>
          </div>
        }
      />
    </>
  );
}

export default ManageSubscription;
